import React from 'react';
import {
  ModalDialog,
  ModalDialogBody,
  ModalDialogButtons,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogNav,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
  Button,
  FlexBox,
  Box,
  Icon,
  useScreenClass,
  Typography,
} from '@vp/swan';

import { PurcsPreview, PreviewModes } from '@vp/react-product-preview';
import PropTypes from 'prop-types';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';

import useTracking from '../hooks/useTracking';
import useSiteData from '../hooks/useSiteData';

import '@vp/react-product-preview/dist/esm/index.css';

interface ProductAttribute {
  key: string;
  value: string;
}

interface PreviewProps {
  previewMode: string;
  purpose: string;
  productKey: string;
  docRefUrl: string;
  productAttributeList: ProductAttribute[];
  openModal: Function;
}

const Preview = ({
  previewMode,
  purpose,
  docRefUrl,
  productAttributeList,
  productKey,
  openModal,
}: PreviewProps) => {
  const { locale } = useSiteData();
  const [cartMessages] = useTranslations(['cartMessages']);

  const optionSelections = productAttributeList.reduce((acc: any, val: any) => {
    const { key, value } = val;
    return {
      ...acc,
      [key]: value,
    };
  }, {});

  return (
    <PurcsPreview
      requestor="orders.cart"
      purpose={purpose}
      coreProductKey={productKey}
      docRefURL={docRefUrl}
      optionSelections={optionSelections}
      onClickMethod={openModal}
      previewMode={previewMode}
      accessibleTextForNext={cartMessages.previewProductAccessibleTextForNext}
      accessibleTextForPrevious={
        cartMessages.previewProductAccessibleTextForPrevious
      }
      locale={locale}
    />
  );
};
Preview.propTypes = {
  previewMode: PropTypes.string.isRequired,
  purpose: PropTypes.string.isRequired,
  productKey: PropTypes.string.isRequired,
  openModal: PropTypes.func.isRequired,
  docRefUrl: PropTypes.string.isRequired,
  productAttributeList: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export interface PurcsModalProps {
  editDesignBlock: React.ReactNode;
  cartItemId: string;
  productName: string;
  productKey: string;
  docRefUrl: string;
  productAttributeList: ProductAttribute[];
}

export const PurcsModal = ({
  editDesignBlock,
  cartItemId,
  docRefUrl,
  productAttributeList,
  productName,
  productKey,
}: PurcsModalProps) => {
  const screenClass = useScreenClass();

  const [cartMessages, sharedActionMessages] = useTranslations([
    'cartMessages',
    'sharedActionMessages',
  ]);

  const [modalIsOpen, setModalIsOpen] = React.useState(false);

  const tracker = useTracking();

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const openModal = () => {
    setModalIsOpen(true);
    tracker.viewLargerImageClicked(cartItemId);
  };

  const isMobile = screenClass === 'xs' || screenClass === 'sm';

  const previewProductAriaLabel = (
    <ReplaceTokens
      tokenizedString={cartMessages.previewProduct}
      substitutions={{ productName }}
    />
  );

  return (
    <>
      <Preview
        purpose="orders.cart"
        previewMode={
          isMobile ? PreviewModes.FirstPageOnly : PreviewModes.Carousel
        }
        productKey={productKey}
        openModal={openModal}
        docRefUrl={docRefUrl}
        productAttributeList={productAttributeList}
      />
      <FlexBox
        alignItems="center"
        flexDirection="column"
        style={{ textAlign: 'center' }}
      >
        <Button skin="unstyled" onClick={openModal}>
          <Icon size="32p" iconType="zoomIn" />
          <Typography component="span" visuallyHidden>
            {cartMessages.viewLargerImage}
          </Typography>
        </Button>
        <Box mt="4">{editDesignBlock}</Box>
      </FlexBox>
      <ModalDialog
        isOpen={modalIsOpen}
        onRequestDismiss={() => setModalIsOpen(false)}
        bodyWidth="capped"
        onlyRenderWhenOpen
      >
        <ModalDialogContent
          style={{ width: '600px' }}
          aria-labelledby="preview-image-dialog-heading"
        >
          <ModalDialogHeader visuallyHidden>
            <ModalDialogTitle id="preview-image-dialog-heading">
              {previewProductAriaLabel}
            </ModalDialogTitle>
          </ModalDialogHeader>
          <ModalDialogNav>
            <ModalDialogCloseButton
              visuallyHiddenLabel={sharedActionMessages.close}
            />
          </ModalDialogNav>
          <ModalDialogBody px="4">
            <Preview
              purpose="design.studio.review"
              previewMode={PreviewModes.Carousel}
              productKey={productKey}
              openModal={openModal}
              docRefUrl={docRefUrl}
              productAttributeList={productAttributeList}
            />
          </ModalDialogBody>
          <ModalDialogFooter>
            <ModalDialogButtons>
              <Button
                size="mini"
                width="full-width"
                skin="primary"
                onClick={() => closeModal()}
              >
                {sharedActionMessages.close}
              </Button>
            </ModalDialogButtons>
          </ModalDialogFooter>
        </ModalDialogContent>
      </ModalDialog>
    </>
  );
};

PurcsModal.propTypes = {
  editDesignBlock: PropTypes.element.isRequired,
  cartItemId: PropTypes.string.isRequired,
  productName: PropTypes.string.isRequired,
  productKey: PropTypes.string.isRequired,
  docRefUrl: PropTypes.string.isRequired,
  productAttributeList: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
};
